import { ButtonLoading } from "@/components/button-loading";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import { useLicenseErrorToast } from "@/hooks/use-license-error-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDateNumeric } from "@/lib/format-amount";
import { LicenseFrequencyDTO, TeamLicenseInventoryDTO } from "@shaarp/common";
import { useState } from "react";

type DialogLicenseResumeConfirmProps = {
  frequency: "monthly" | "annual";
  freqData: LicenseFrequencyDTO;
  onClose: () => void;
  onConfirmed: (updatedInventory: TeamLicenseInventoryDTO) => void;
};

/**
 * Confirmation dialog for resuming a scheduled-to-cancel license subscription.
 */
export const DialogLicenseResumeConfirm = ({
  frequency,
  freqData,
  onClose,
  onConfirmed,
}: DialogLicenseResumeConfirmProps) => {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const { toast } = useToast();
  const showLicenseError = useLicenseErrorToast();
  const idempotencyKey = useIdempotencyKey();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDate = (date: Date | null | undefined) =>
    formatDateNumeric(date, i18n.language);

  const frequencyLabel =
    frequency === "monthly"
      ? t("team.licenseManagement.monthly")
      : t("team.licenseManagement.annual");

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      const { data } = await api.post<TeamLicenseInventoryDTO>(
        "/my/team/licenses/resume",
        { frequency },
        { headers: { "Idempotency-Key": idempotencyKey } }
      );
      toast({
        variant: "default",
        title: t("team.licenseManagement.title"),
        description: t("team.licenseManagement.resumeDialog.successToast", {
          frequency: frequencyLabel,
        }),
      });
      onConfirmed(data);
    } catch (error) {
      showLicenseError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[460px] w-full">
        <DialogHeader>
          <DialogTitle>
            {t("team.licenseManagement.resumeDialog.title", {
              frequency: frequencyLabel,
            })}
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <p className="text-sm text-muted-foreground">
          {t("team.licenseManagement.resumeDialog.description", {
            frequency: frequencyLabel,
            date: formatDate(freqData.currentPeriodEndsAt),
          })}
        </p>

        <Separator />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t("team.licenseManagement.resumeDialog.back")}
          </Button>
          <ButtonLoading
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleConfirm}
          >
            {t("team.licenseManagement.resumeDialog.confirm")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
